import { ColorTheme } from "shared/theme/theme";

export function hexToRgb(_hex: string): { r: number; g: number; b: number } {
  // Remove the leading # if present
  const hex = _hex.replace(/^#/, "");

  // Check if the string is either 3 or 6 characters long
  if (hex.length !== 3 && hex.length !== 6) {
    throw new Error(
      "Invalid hex color format. String must be either 3 or 6 characters long after #",
    );
  }

  // If the string is 3 characters long, convert it to 6 characters
  const fullHex =
    hex.length === 3
      ? hex
          .split("")
          .map((char) => char + char)
          .join("")
      : hex;

  // Convert the hex values to decimal
  const num = parseInt(fullHex, 16);
  if (Number.isNaN(num)) {
    throw new Error("Invalid hex color format. Could not convert to a number");
  }

  const r = Math.floor(num / (256 * 256)) % 256;
  const g = Math.floor(num / 256) % 256;
  const b = num % 256;

  return { r, g, b };
}

export const isHex = (_hex: string) => {
  try {
    hexToRgb(_hex);
    return true;
  } catch {
    return false;
  }
};

export const interpolateColors = (
  startColorHex: string,
  endColorHex: string,
  interpolant: number, // must be in the range [0, 1]
) => {
  if (interpolant < 0 || interpolant > 1) {
    throw new Error(`Interpolant must be between 0 and 1`);
  }
  const startColor = hexToRgb(startColorHex);
  const endColor = hexToRgb(endColorHex);
  const interpolate = (color: "r" | "g" | "b") =>
    Math.round(
      startColor[color] + (endColor[color] - startColor[color]) * interpolant,
    );
  const [r, g, b] = [interpolate("r"), interpolate("g"), interpolate("b")];
  const toHex = (value: number) => value.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Use "base" | "main" | "secondary" | "tertiary" for the most common colors
 * Use `keyof Theme` (e.g. "colorAlarmFill") for custom colors
 */
export type VariantColor =
  | "base"
  | "main"
  | "secondary"
  | "tertiary"
  | keyof ColorTheme;
export const variantToColor = (variant: VariantColor, theme: ColorTheme) => {
  const colorMap = {
    [variant]: theme[variant as keyof ColorTheme], // Picks this if variant is not base, main, secondary or tertiary.
    base: theme.colorBackgroundBase,
    main: theme.colorBackgroundMain,
    secondary: theme.colorBackgroundSecondary,
    tertiary: theme.colorBackgroundTertiary,
  };
  return colorMap[variant];
};
